import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AlertCircle, FileText, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { usePostTreatmentStatus } from '@/hooks/usePostTreatmentStatus';

interface SmartInsightsProps {
  patients: any[];
}

const SmartInsights: React.FC<SmartInsightsProps> = ({ patients }) => {
  const navigate = useNavigate();
  const { checkPostTreatmentExists } = usePostTreatmentStatus();

  // Identify patients who need attention
  const noTreatmentPlanPatients = patients.filter(p => 
    !p.treatmentPlans || p.treatmentPlans.length === 0 || 
    p.treatmentPlans.every(tp => tp.status === 'pending')
  ).slice(0, 5);

  // Get completed patients without post-treatment reports
  const completedWithoutReportPatients = patients.filter(p => {
    // Convert patientId to string if it's not already
    const patientIdStr = String(p.id);
    return p.status === 'completed' && checkPostTreatmentExists(patientIdStr) === 'none';
  }).slice(0, 5);

  // Find follow-up patients without reports
  const followupWithoutReportPatients = patients.filter(p => {
    // Convert patientId to string if it's not already
    const patientIdStr = String(p.id);
    return p.status === 'follow-up' && checkPostTreatmentExists(patientIdStr) === 'none';
  }).slice(0, 5);

  const handleCreateReport = (patientId: string | number) => {
    // Ensure patientId is a string
    const patientIdStr = String(patientId);
    navigate(`/doctor-area/post-treatment/${patientIdStr}`);
  };

  const handleCreatePlan = (patientId: string | number) => {
    // Ensure patientId is a string
    const patientIdStr = String(patientId);
    navigate(`/doctor-area/treatment-plans/create/${patientIdStr}`);
  };

  if (noTreatmentPlanPatients.length === 0 && 
      completedWithoutReportPatients.length === 0 && 
      followupWithoutReportPatients.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Smart Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-6 text-center text-muted-foreground">
            <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p>Great job! All your patients have been attended to properly.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Smart Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {noTreatmentPlanPatients.length > 0 && (
          <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
            <div className="flex gap-3 items-start">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">
                  {noTreatmentPlanPatients.length} {noTreatmentPlanPatients.length === 1 ? 'patient has' : 'patients have'} no treatment plan yet
                </p>
                <div className="mt-2 space-y-2">
                  {noTreatmentPlanPatients.map(patient => (
                    <div key={patient.id} className="flex justify-between items-center text-sm">
                      <span>{patient.name}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs"
                        onClick={() => handleCreatePlan(patient.id)}
                      >
                        Create Plan
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {completedWithoutReportPatients.length > 0 && (
          <div className="p-3 border rounded-lg bg-blue-50 border-blue-200">
            <div className="flex gap-3 items-start">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">
                  {completedWithoutReportPatients.length} completed {completedWithoutReportPatients.length === 1 ? 'patient has' : 'patients have'} no post-treatment report
                </p>
                <div className="mt-2 space-y-2">
                  {completedWithoutReportPatients.map(patient => (
                    <div key={patient.id} className="flex justify-between items-center text-sm">
                      <span>{patient.name}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs"
                        onClick={() => handleCreateReport(patient.id)}
                      >
                        Create Report
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {followupWithoutReportPatients.length > 0 && (
          <div className="p-3 border rounded-lg bg-purple-50 border-purple-200">
            <div className="flex gap-3 items-start">
              <Calendar className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">
                  {followupWithoutReportPatients.length} follow-up {followupWithoutReportPatients.length === 1 ? 'patient has' : 'patients have'} no report yet
                </p>
                <div className="mt-2 space-y-2">
                  {followupWithoutReportPatients.map(patient => (
                    <div key={patient.id} className="flex justify-between items-center text-sm">
                      <span>{patient.name}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs"
                        onClick={() => handleCreateReport(patient.id)}
                      >
                        Create Report
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartInsights;
